// Stop button behavior for an in-flight turn. POSTs the exact durable turn
// cancel when client identity is available (legacy session cancel otherwise),
// then keeps the composer locked until turn-status reports a durable terminal
// state. The
// cancel request itself is only an acknowledgement; a deployment or race can
// delay Temporal's cancellation after the HTTP call returns.

import { useCallback, useState } from 'react';
import { requestTurnCancellation } from '../api';
import { useAgentForgeStore } from '../stores';
import { abortTurnSubmission } from './turnSubmissionRegistry';

export interface StopTurn {
  /** Request cancellation of the active session's in-flight turn. */
  stop: () => Promise<void>;
  /** True until durable status confirms the turn is terminal. */
  stopping: boolean;
}

export const useStopTurn = (): StopTurn => {
  const [requestPending, setRequestPending] = useState(false);
  const cancelling = useAgentForgeStore((s) => {
    const sid = s.activeSessionId;
    return sid ? s.activeTurnsBySession[sid]?.phase === 'cancelling' : false;
  });

  const stop = useCallback(async () => {
    const state = useAgentForgeStore.getState();
    const sessionId = state.activeSessionId;
    const activeTurn = sessionId
      ? state.activeTurnsBySession[sessionId]
      : undefined;
    if (
      !sessionId ||
      !activeTurn ||
      requestPending ||
      activeTurn.phase === 'cancelling'
    )
      return;
    state.upsertActiveTurn(sessionId, { phase: 'cancelling' });
    // If the POST body is still uploading, the backend has no durable row to
    // cancel yet. Abort locally first, then always issue exact server cancel
    // as well to cover the race where acceptance just committed.
    abortTurnSubmission(sessionId, activeTurn.turnId);
    setRequestPending(true);
    try {
      await requestTurnCancellation({
        sessionId,
        turnId: activeTurn.turnId,
        identityMode: activeTurn.identityMode,
      });
    } catch {
      // The request may have reached the server before the connection failed.
      // Status reconciliation remains authoritative and keeps polling.
    } finally {
      setRequestPending(false);
    }
  }, [requestPending]);

  return { stop, stopping: requestPending || cancelling };
};
